/**
 * Astrologer Branding Persistence Service
 * 
 * Remembers astrologer credentials (Name, Title, Sansthan, Phone/WhatsApp, Location)
 * permanently so the user is never repeatedly prompted to enter this information.
 */

import { getPracticeSettings, savePracticeSettings } from './practiceManagementService';
import { PracticeProfileSettings } from '../types/practiceManagement';

export interface AstrologerBranding {
  name: string;
  title: string;
  sansthan: string;
  phone: string;
  location: string;
  email?: string;
  registrationNumber?: string;
  customHeaderSlogan?: string;
}

const BRANDING_STORAGE_KEY = 'astroveda_saved_astrologer_branding_v1';

export const DEFAULT_ASTROLOGER_BRANDING: AstrologerBranding = {
  name: '',
  title: '',
  sansthan: '',
  phone: '',
  location: '',
  email: '',
  registrationNumber: '',
  customHeaderSlogan: ''
};

// Known historical placeholder values to clear out so they are never assumed
const ASSUMED_NAMES = ['acharya rishi raj mishra', 'acharya vidyadhar shastri'];
const ASSUMED_PHONES = ['+91 97178 38787', '9717838787', '+91 98765 43210', '9876543210'];
const ASSUMED_SANSTHANS = ['vaidik jyotish sewa sansthan', 'vedang jyotisham vedic consulting'];

/**
 * Retrieve saved astrologer branding profile.
 * Falls back to practice settings, or blank values if nothing is explicitly set.
 */
export function getSavedAstrologerBranding(): AstrologerBranding {
  try {
    const raw = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const rawName = (parsed.name || '').trim();
        const rawPhone = (parsed.phone || '').trim();
        const rawSansthan = (parsed.sansthan || '').trim();
        const rawTitle = (parsed.title || '').trim();
        const rawLocation = (parsed.location || '').trim();
        const rawEmail = (parsed.email || '').trim();
        const rawReg = (parsed.registrationNumber || '').trim();
        const rawSlogan = (parsed.customHeaderSlogan || '').trim();

        // If stored values were the legacy mock/assumed placeholders, clear them
        const cleanName = ASSUMED_NAMES.includes(rawName.toLowerCase()) ? '' : rawName;
        const cleanPhone = ASSUMED_PHONES.includes(rawPhone.toLowerCase()) ? '' : rawPhone;
        const cleanSansthan = ASSUMED_SANSTHANS.includes(rawSansthan.toLowerCase()) ? '' : rawSansthan;
        const cleanTitle = (rawTitle.toLowerCase() === 'vaidik jyotish & vastu acharya' || rawTitle.toLowerCase() === 'jyotish visharad & vedic counselor') ? '' : rawTitle;
        const cleanLocation = (rawLocation.toLowerCase().includes('vikaspuri') || rawLocation.toLowerCase().includes('varanasi / new delhi')) ? '' : rawLocation;
        const cleanEmail = (rawEmail.toLowerCase().includes('acharyarishi') || rawEmail.toLowerCase().includes('acharya.vidyadhar')) ? '' : rawEmail;
        const cleanReg = rawReg === 'VJSS/DEL/1998/412' ? '' : rawReg;
        const cleanSlogan = rawSlogan.includes('The Eye of the Vedas') ? '' : rawSlogan;

        return {
          name: cleanName,
          title: cleanTitle,
          sansthan: cleanSansthan,
          phone: cleanPhone,
          location: cleanLocation,
          email: cleanEmail,
          registrationNumber: cleanReg,
          customHeaderSlogan: cleanSlogan
        };
      }
    }

    // Secondary check: check practice management settings if user configured it
    const practiceSettings = getPracticeSettings();
    if (practiceSettings) {
      const pName = (practiceSettings.primaryPractitionerName || '').trim();
      const pPhone = (practiceSettings.phone || '').trim();
      const isAssumedPName = ASSUMED_NAMES.includes(pName.toLowerCase());
      const isAssumedPPhone = ASSUMED_PHONES.includes(pPhone.toLowerCase());

      if ((!isAssumedPName && pName) || (!isAssumedPPhone && pPhone)) {
        return {
          name: isAssumedPName ? '' : pName,
          title: practiceSettings.practitionerTitle || '',
          sansthan: ASSUMED_SANSTHANS.includes((practiceSettings.practiceName || '').toLowerCase().trim()) ? '' : (practiceSettings.practiceName || ''),
          phone: isAssumedPPhone ? '' : pPhone,
          location: practiceSettings.location || '',
          email: practiceSettings.email || '',
          registrationNumber: '',
          customHeaderSlogan: practiceSettings.tagline || ''
        };
      }
    }
  } catch (e) {
    console.error('Failed to load astrologer branding profile:', e);
  }
  return DEFAULT_ASTROLOGER_BRANDING;
}

/**
 * Permanently save astrologer branding profile.
 * Also synchronizes with Practice Management settings.
 */
export function saveAstrologerBranding(branding: Partial<AstrologerBranding>): AstrologerBranding {
  try {
    const current = getSavedAstrologerBranding();
    const updated: AstrologerBranding = {
      name: branding.name !== undefined ? branding.name.trim() : current.name,
      title: branding.title !== undefined ? branding.title.trim() : current.title,
      sansthan: branding.sansthan !== undefined ? branding.sansthan.trim() : current.sansthan,
      phone: branding.phone !== undefined ? branding.phone.trim() : current.phone,
      location: branding.location !== undefined ? branding.location.trim() : current.location,
      email: branding.email !== undefined ? branding.email.trim() : current.email,
      registrationNumber: branding.registrationNumber !== undefined ? branding.registrationNumber.trim() : current.registrationNumber,
      customHeaderSlogan: branding.customHeaderSlogan !== undefined ? branding.customHeaderSlogan.trim() : current.customHeaderSlogan
    };

    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(updated));

    // Keep Practice Profile Settings in sync
    try {
      const currentPracticeSettings = getPracticeSettings();
      const newPracticeSettings: PracticeProfileSettings = {
        ...currentPracticeSettings,
        primaryPractitionerName: updated.name,
        practitionerTitle: updated.title,
        practiceName: updated.sansthan,
        phone: updated.phone,
        location: updated.location,
        email: updated.email || currentPracticeSettings.email
      };
      savePracticeSettings(newPracticeSettings);
    } catch {
      // Non-blocking sync
    }

    return updated;
  } catch (e) {
    console.error('Failed to save astrologer branding profile:', e);
    return DEFAULT_ASTROLOGER_BRANDING;
  }
}
